import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { healthAPI } from '../services/api';
import { getContextualError } from '../utils/errorMessages';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Activity, Heart, Pill, Camera, Droplets, Footprints, Flame, Loader2, Trash2, Upload, UtensilsCrossed, Crown, Sparkles, CreditCard, LogOut, FileText, User, Receipt, Calendar as CalendarIcon, ChevronRight, Plus, Smile } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import EditProfileModal from '../components/EditProfileModal';
import PaymentHistoryModal from '../components/PaymentHistoryModal';
import HealthCalendar from '../components/HealthCalendar';
import WomensHealth from '../components/WomensHealth';
import { useToast } from '../hooks/use-toast';
// REMOVED: notificationService - frontend should NOT trigger push notifications
// Push notifications are sent ONLY by backend schedulers
// NEW: Enhanced Health Mode Components
// HealthInsightsDashboard removed per user request
import SmartMealPlanner from '../components/SmartMealPlanner';
import PersonalizedWorkout from '../components/PersonalizedWorkout';
// DailyRecommendations removed per user request
import MedicineInteractionChecker from '../components/MedicineInteractionChecker';
import QuickActionButton from '../components/QuickActionButton';
import HealthAssistantUI from '../components/HealthAssistantUI';
import MoodLogger from '../components/MoodLogger';
// 🧠 Emotional Mode Awareness
import useEmotionalMode from '../hooks/useEmotionalMode';

const HealthMode = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, refreshUser } = useAuth();
  const { toast } = useToast();
  const { t } = useLocale();
  // 🧠 Emotional Mode Hook
  const { emotionalMode, ambientEmotional, shouldShowStreaks } = useEmotionalMode();
  const [subscription, setSubscription] = React.useState(null);
  
  // Get initial tab from URL parameter (?tab=nutrition, etc.)
  const getInitialTab = () => {
    const tab = searchParams.get('tab');
    const validTabs = ['dashboard', 'nutrition', 'womens-health', 'history'];
    if (tab && validTabs.includes(tab)) {
      return tab;
    }
    return 'dashboard';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab());
  
  // Debug: Log user data to check gender - DETAILED
  useEffect(() => {
    console.log('=== HEALTH MODE USER DEBUG ===');
    console.log('User object:', user);
    if (user) {
      console.log('User.gender value:', user.gender);
      console.log('User.gender type:', typeof user.gender);
      console.log('User.gender toLowerCase:', user.gender?.toLowerCase());
      console.log('Is female?:', user.gender?.toLowerCase() === 'female');
      console.log('Full user object:', JSON.stringify(user, null, 2));
    } else {
      console.error('❌ User is NULL or UNDEFINED');
    }
    console.log('==============================');
  }, [user]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [medicineDialogOpen, setMedicineDialogOpen] = useState(false);
  const [foodDialogOpen, setFoodDialogOpen] = useState(false);
  const [waterDialogOpen, setWaterDialogOpen] = useState(false);
  const [workoutDialogOpen, setWorkoutDialogOpen] = useState(false);
  const [symptomDialogOpen, setSymptomDialogOpen] = useState(false);
  const [mealDialogOpen, setMealDialogOpen] = useState(false);
  const [moodLoggerOpen, setMoodLoggerOpen] = useState(false);
  // NEW: Enhanced features state
  const [showMealPlanner, setShowMealPlanner] = useState(false);
  const [showWorkoutLibrary, setShowWorkoutLibrary] = useState(false);
  const [showMedicineChecker, setShowMedicineChecker] = useState(false);
  const [processingFood, setProcessingFood] = useState(false);
  const [foodAnalysis, setFoodAnalysis] = useState(null);
  const [newMedicine, setNewMedicine] = useState({
    name: '',
    dosage: '',
    frequency: 'Daily',
    time: '',
    duration: 'Ongoing',
    reminderEnabled: true
  });
  const [workoutData, setWorkoutData] = useState({
    type: '',
    duration: '',
    caloriesBurned: ''
  });
  const [symptomData, setSymptomData] = useState({
    symptoms: '',
    severity: 'mild',
    notes: ''
  });
  const [mealDescription, setMealDescription] = useState('');
  const [foodImage, setFoodImage] = useState(null);
  const [foodImagePreview, setFoodImagePreview] = useState(null);
  const [loggedFoods, setLoggedFoods] = useState([]);
  const [assistantDialogOpen, setAssistantDialogOpen] = useState(false);
  const [assistantMessage, setAssistantMessage] = useState('');
  const [assistantResponse, setAssistantResponse] = useState(null);
  const [processingAssistant, setProcessingAssistant] = useState(false);
  const [editFoodDialogOpen, setEditFoodDialogOpen] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const [parsedFoodData, setParsedFoodData] = useState(null); // Stores parsed nutrition for preview
  const [waterReminderSettings, setWaterReminderSettings] = useState(null);
  const [showWaterReminder, setShowWaterReminder] = useState(false);
  // REMOVED: notification permission state - we don't trigger push from frontend
  // const [notificationPermission, setNotificationPermission] = useState(notificationService.getPermission());
  // const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  // REMOVED: useEffect that checked notification status
  // Frontend should NEVER trigger push notifications
  // Only backend schedulers send push notifications

  useEffect(() => {
    loadHealthData();
    loadSubscription();
    loadWaterReminders();
    // REMOVED: checkWaterReminder() - water reminders come from backend scheduler only
  }, []);

  const loadWaterReminders = async () => {
    try {
      const response = await healthAPI.getWaterReminders();
      setWaterReminderSettings(response.data);
    } catch (error) {
      console.error('Error loading water reminders:', error);
    }
  };

  // REMOVED: getWaterReminderMessage - not needed since we don't trigger notifications from frontend
  // REMOVED: checkWaterReminder - water reminders come from backend scheduler only

  const loadSubscription = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/subscription`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadHealthData = async () => {
    try {
      const [healthResponse, medicinesResponse, foodsResponse, workoutsResponse] = await Promise.all([
        healthAPI.getTodayData(),
        healthAPI.getMedicines(),
        healthAPI.getFoods(),
        healthAPI.getWorkouts()
      ]);
      
      const healthDataWithWorkouts = {
        ...(healthResponse.data || { steps: 0, calories: 0, water: 0, meals: [], symptoms: [] }),
        workouts: workoutsResponse.data?.workouts || []
      };
      
      setHealthData(healthDataWithWorkouts);
      setMedicines(medicinesResponse.data || []);
      setLoggedFoods(foodsResponse.data || []);
    } catch (error) {
      console.error('Error loading health data:', error);
      setHealthData({ steps: 0, calories: 0, water: 0, workouts: [], meals: [], symptoms: [] });
      setMedicines([]);
      setLoggedFoods([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedicine = async () => {
    // Detailed validation with specific error messages
    if (!newMedicine.name.trim()) {
      toast({ title: 'Missing Medicine Name', description: 'Please enter the medicine name', variant: 'destructive' });
      return;
    }
    if (!newMedicine.dosage.trim()) {
      toast({ title: 'Missing Dosage', description: 'Please enter the dosage (e.g., "1 tablet", "10mg")', variant: 'destructive' });
      return;
    }
    if (!newMedicine.time) {
      toast({ title: 'Missing Time', description: 'Please select a reminder time', variant: 'destructive' });
      return;
    }
    
    try {
      console.log('Adding medicine:', newMedicine);
      await healthAPI.addMedicine(newMedicine);
      toast({ title: '✅ Medicine Added!', description: `${newMedicine.name} added to reminders` });
      setMedicineDialogOpen(false);
      setNewMedicine({ name: '', dosage: '', frequency: 'Daily', time: '', duration: 'Ongoing', reminderEnabled: true });
      loadHealthData();
    } catch (error) {
      console.error('Error adding medicine:', error);
      toast({ title: 'Unable to Add Medicine', description: error?.response?.data?.detail || 'Please try again', variant: 'destructive' });
    }
  };

  const handleMarkTaken = async (medicine) => {
    try {
      const response = await healthAPI.markMedicineTaken(medicine.id);
      const streak = response?.data?.streak || 1;
      const timeNow = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      // Encouraging messages based on streak and frequency
      const messages = {
        daily: {
          1: { title: '💊 Great Start!', desc: `${medicine.name} taken. Keep it up!` },
          3: { title: '🔥 3-Day Streak!', desc: `${medicine.name} - You're building a healthy habit!` },
          7: { title: '🌟 1 Week Streak!', desc: `${medicine.name} - Amazing consistency! A full week!` },
          14: { title: '🏆 2 Week Champion!', desc: `${medicine.name} - You're crushing it!` },
          30: { title: '👑 Monthly Master!', desc: `${medicine.name} - 30 days! Incredible dedication!` },
          default: { title: '✅ Medicine Taken!', desc: `${medicine.name} at ${timeNow}` }
        },
        twice_daily: {
          default: { title: '✅ Dose Recorded!', desc: `${medicine.name} - Next dose in ~12 hours` }
        },
        weekly: {
          default: { title: '📅 Weekly Dose Done!', desc: `${medicine.name} - See you next week!` }
        },
        as_needed: {
          default: { title: '✅ Recorded!', desc: `${medicine.name} taken as needed at ${timeNow}` }
        }
      };
      
      const freq = medicine.frequency?.toLowerCase().replace(' ', '_') || 'daily';
      const freqMessages = messages[freq] || messages.daily;
      
      let message;
      if (freq === 'daily' || freq === 'once_daily') {
        message = freqMessages[streak] || freqMessages.default;
      } else {
        message = freqMessages.default;
      }
      
      // Show encouraging toast with streak info
      // 🧠 Emotional Mode: Suppress streak pressure in toasts
      toast({
        title: message.title,
        description: (
          <div className="space-y-1">
            <p>{message.desc}</p>
            {freq === 'daily' && streak > 1 && shouldShowStreaks() && (
              <p className="text-xs text-green-600 font-medium">🔥 {streak}-day streak!</p>
            )}
            <p className="text-xs text-gray-500">Logged at {timeNow}</p>
          </div>
        ),
        duration: 4000
      });
      
      loadHealthData();
    } catch (error) {
      toast({ title: 'Unable to Update', description: getContextualError(error, 'health.addMedicine'), variant: 'destructive' });
    }
  };

  const handleDeleteMedicine = async (medicine) => {
    try {
      await healthAPI.deleteMedicine(medicine.id);
      toast({ title: '🗑️ Removed', description: `${medicine.name} removed` });
      loadHealthData();
    } catch (error) {
      toast({ title: 'Unable to Delete', description: getContextualError(error, 'health.addMedicine'), variant: 'destructive' });
    }
  };

  const getWaterLoggedMessage = (totalWater, dailyGoal) => {
    const progress = totalWater / dailyGoal;
    
    if (totalWater === 1) {
      return {
        title: '💧 First Glass Down!',
        description: 'Perfect start! Your hydration journey begins now. Every drop counts! 🌟'
      };
    } else if (totalWater === dailyGoal) {
      return {
        title: '🎉 Daily Goal Achieved!',
        description: `${dailyGoal} glasses complete! You're a hydration superstar! Your body loves you! 🏆✨`
      };
    } else if (progress >= 0.75) {
      return {
        title: '🔥 You\'re On Fire!',
        description: `${totalWater} glasses! Just ${dailyGoal - totalWater} more to reach your goal. Victory is near! 💪`
      };
    } else if (progress >= 0.5) {
      return {
        title: '⚡ Halfway Champion!',
        description: `${totalWater} glasses logged! You're crushing it! Keep the momentum going! 🌊`
      };
    } else if (progress >= 0.25) {
      return {
        title: '🌟 Great Progress!',
        description: `${totalWater} glasses down! You're building an awesome hydration habit! Keep it up! 💙`
      };
    } else {
      return {
        title: '✨ Nicely Done!',
        description: `${totalWater} glasses! You're on your way. Stay consistent, stay hydrated! 💧`
      };
    }
  };

  const handleAddWater = async () => {
    try {
      const response = await healthAPI.logWater(1);
      const totalWater = response.data.total_water;
      setHealthData({ ...healthData, water: totalWater });
      
      // Get dynamic message based on progress
      const dailyGoal = waterReminderSettings?.daily_goal || 8;
      const message = getWaterLoggedMessage(totalWater, dailyGoal);
      
      toast({ 
        title: message.title, 
        description: message.description,
        duration: 5000
      });
      setWaterDialogOpen(false);
    } catch (error) {
      toast({ title: 'Unable to Log Water', description: getContextualError(error, 'health.logWater'), variant: 'destructive' });
    }
  };

  const handleLogWorkout = async () => {
    if (!workoutData.type.trim() || !workoutData.duration) {
      toast({ title: 'Error', description: 'Please fill in workout details', variant: 'destructive' });
      return;
    }

    try {
      await healthAPI.logWorkout({
        type: workoutData.type,
        duration: parseInt(workoutData.duration),
        calories: parseInt(workoutData.caloriesBurned) || 0
      });
      
      toast({ title: '🏋️ Workout Logged!', description: `${workoutData.type} - ${workoutData.duration} mins` });
      setWorkoutDialogOpen(false);
      setWorkoutData({ type: '', duration: '', caloriesBurned: '' });
      loadHealthData();
    } catch (error) {
      toast({ title: 'Unable to Log Workout', description: getContextualError(error, 'health.logWorkout'), variant: 'destructive' });
    }
  };

  const handleLogSymptom = async () => {
    if (!symptomData.symptoms.trim()) {
      toast({ title: 'Error', description: 'Please describe your symptoms', variant: 'destructive' });
      return;
    }

    try {
      await healthAPI.logSymptoms({
        symptoms: symptomData.symptoms,
        severity: symptomData.severity,
        notes: symptomData.notes
      });
      
      toast({ title: '🌡️ Symptoms Logged!', description: 'Your symptoms have been recorded' });
      setSymptomDialogOpen(false);
      setSymptomData({ symptoms: '', severity: 'mild', notes: '' });
      loadHealthData();
    } catch (error) {
      toast({ title: 'Unable to Log Symptoms', description: getContextualError(error, 'health.logFood'), variant: 'destructive' });
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFoodImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFoodImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      toast({
        title: '📷 Image Selected',
        description: `${file.name} - Ready for AI analysis`
      });
    }
  };

  const handleDeleteFood = async (foodId) => {
    try {
      await healthAPI.deleteFood(foodId);
      toast({ title: '🗑️ Food Deleted', description: 'Food entry removed from your diary' });
      loadHealthData();
    } catch (error) {
      toast({ title: 'Unable to Delete', description: getContextualError(error, 'health.logFood'), variant: 'destructive' });
    }
  };

  const handleEditFood = (food) => {
    setEditingFood(food);
    setEditFoodDialogOpen(true);
  };

  const handleSaveFoodEdit = async () => {
    if (!editingFood) return;
    
    try {
      await healthAPI.updateFood(editingFood.id, {
        name: editingFood.name,
        calories: parseFloat(editingFood.calories),
        protein: parseFloat(editingFood.protein),
        carbs: parseFloat(editingFood.carbs),
        fat: parseFloat(editingFood.fat),
        healthScore: editingFood.healthScore ? parseInt(editingFood.healthScore) : null,
        analysis: editingFood.analysis
      });
      
      toast({
        title: '✅ Food Updated',
        description: 'Your changes have been saved'
      });
      
      setEditFoodDialogOpen(false);
      setEditingFood(null);
      loadHealthData();
    } catch (error) {
      toast({ title: 'Unable to Update', description: getContextualError(error, 'health.logFood'), variant: 'destructive' });
    }
  };

  const handleAskAssistant = async () => {
    if (!assistantMessage.trim()) {
      toast({ title: 'Error', description: 'Please enter a question', variant: 'destructive' });
      return;
    }

    setProcessingAssistant(true);
    console.log('🩺 Asking health assistant:', assistantMessage);
    
    try {
      const response = await healthAPI.medicalAssistant(assistantMessage);
      console.log('✅ Health assistant response:', response);
      
      setAssistantResponse(response.data.response);
      toast({ title: '🩺 Health Assistant', description: 'AI has analyzed your health data and provided advice' });
    } catch (error) {
      console.error('❌ Error with medical assistant:', error);
      console.error('Error details:', {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data
      });
      
      if (error.response?.status === 401) {
        toast({
          title: '🔐 Session Expired',
          description: 'Please log out and log in again to continue.',
          variant: 'destructive'
        });
      } else if (error.response?.status === 402) {
        toast({
          title: '❌ Insufficient Credits',
          description: 'You need 1 credit for health assistant. Please upgrade or buy credits.',
          variant: 'destructive'
        });
      } else {
        toast({ 
          title: 'Error', 
          description: getContextualError(error, 'health.getMedicalAdvice'), 
          variant: 'destructive' 
        });
      }
    } finally {
      setProcessingAssistant(false);
    }
  };

  const handleAnalyzeFood = async () => {
    if (!mealDescription.trim() && !foodImage) {
      toast({ title: 'Error', description: 'Please upload an image or describe your meal', variant: 'destructive' });
      return;
    }

    setProcessingFood(true);
    try {
      let response;
      
      if (foodImage) {
        // Analyze food image
        const formData = new FormData();
        formData.append('file', foodImage);
        
        const apiResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/analyze-food-image`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        if (!apiResponse.ok) {
          throw new Error('Failed to analyze food image');
        }
        
        response = await apiResponse.json();
      } else {
        // Analyze text description
        const apiResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/analyze-food-text`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ description: mealDescription })
        });
        
        if (!apiResponse.ok) {
          throw new Error('Failed to analyze food description');
        }
        
        response = await apiResponse.json();
      }
      
      console.log('✅ AI Food Response received:', response);
      console.log('📝 Response content:', response.response);
      
      if (!response.response || response.response.trim().length === 0) {
        throw new Error('AI returned empty response. Please try again.');
      }
      
      // Set the analysis to display
      setFoodAnalysis(response.response);
      
      // Parse nutritional data from AI response for preview
      const caloriesMatch = response.response.match(/\*\*Calories:\*\*\s*(\d+)/);
      const proteinMatch = response.response.match(/\*\*Protein:\*\*\s*(\d+\.?\d*)g/);
      const carbsMatch = response.response.match(/\*\*Carbs:\*\*\s*(\d+\.?\d*)g/);
      const fatMatch = response.response.match(/\*\*Fat:\*\*\s*(\d+\.?\d*)g/);
      const fiberMatch = response.response.match(/\*\*Fiber:\*\*\s*(\d+\.?\d*)g/);
      const sugarMatch = response.response.match(/\*\*Sugar:\*\*\s*(\d+\.?\d*)g/);
      const healthScoreMatch = response.response.match(/\*\*Health Score:\*\*\s*(\d+)\/10/);
      const foodNameMatch = response.response.match(/\*\*Food:\*\*\s*(.+)/);
      
      const parsedData = {
        name: foodNameMatch ? foodNameMatch[1].trim() : (mealDescription || 'Food from image'),
        calories: caloriesMatch ? parseInt(caloriesMatch[1]) : 0,
        protein: proteinMatch ? parseFloat(proteinMatch[1]) : 0,
        carbs: carbsMatch ? parseFloat(carbsMatch[1]) : 0,
        fat: fatMatch ? parseFloat(fatMatch[1]) : 0,
        fiber: fiberMatch ? parseFloat(fiberMatch[1]) : 0,
        sugar: sugarMatch ? parseFloat(sugarMatch[1]) : 0,
        healthScore: healthScoreMatch ? parseInt(healthScoreMatch[1]) : null,
        analysis: response.response,
        imageUrl: foodImagePreview || null
      };
      
      console.log('📊 Parsed nutrition for preview:', parsedData);
      
      // Store parsed data for the preview - user will click "Add to Diary" to save
      setParsedFoodData(parsedData);
      
      toast({ 
        title: '✅ Analysis Complete!', 
        description: 'Review the results and add to your food diary.'
      });
      
    } catch (error) {
      console.error('Error analyzing food:', error);
      setFoodAnalysis(null);
      setParsedFoodData(null);
      toast({ title: 'Error', description: getContextualError(error, 'health.analyzeFood'), variant: 'destructive' });
    } finally {
      setProcessingFood(false);
    }
  };

  // Add analyzed food to diary
  const handleAddFoodToDiary = async () => {
    if (!parsedFoodData) return;
    
    try {
      await healthAPI.logFood(parsedFoodData);
      
      toast({ 
        title: '✅ Added to Diary!', 
        description: `${parsedFoodData.name} - ${parsedFoodData.calories} cal added to today's totals`
      });
      
      // Reload data and close dialog
      await loadHealthData();
      setFoodDialogOpen(false);
      setMealDescription('');
      setFoodImage(null);
      setFoodImagePreview(null);
      setFoodAnalysis(null);
      setParsedFoodData(null);
    } catch (error) {
      console.error('Error saving food:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to save food to diary. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Clear food analysis to try again
  const handleClearFoodAnalysis = () => {
    setFoodAnalysis(null);
    setParsedFoodData(null);
  };

  // Show loading state while user data is being fetched
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('health.loadingHealthMode')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white relative overflow-x-hidden overflow-y-auto">
      <header className="glass-strong border-b border-white/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/home')} className="hover:bg-teal-100/60 transition-all duration-200">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center text-white text-xl shadow-soft">
                  💪
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">{t('health.title')}</h1>
                  <p className="text-xs text-gray-500 font-medium">{t('cards.healthDesc')} ✨</p>
                </div>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-700" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80" align="end">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex items-center space-x-3 p-2">
                    <div className="h-9 w-9 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {subscription && (
                  <>
                    <div className="px-3 py-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('health.subscription')}</span>
                        {subscription.subscription?.plan_id !== 'free' ? (
                          <div className="flex items-center gap-1 bg-teal-100 text-teal-800 px-2 py-1 rounded-full">
                            <Crown className="h-3 w-3" />
                            <span className="text-xs font-medium">{t('health.pro')}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-xs">{t('health.freePlan')}</Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('health.aiAccess')}</span>
                        {subscription.subscription?.plan_id !== 'free' ? (
                          <div className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-teal-600" />
                            <span className="text-sm font-semibold text-teal-600">{t('health.unlimited')}</span>
                          </div>
                        ) : (
                          <span className="text-sm font-semibold text-gray-900">{subscription.credits || 0} {t('health.credits')}</span>
                        )}
                      </div>
                      {subscription.subscription?.plan_id === 'free' && (
                        <Button
                          onClick={() => navigate('/pricing')}
                          size="sm"
                          className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                        >
                          <Sparkles className="h-3 w-3 mr-2" />
                          Upgrade to Pro
                        </Button>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                  </>
                )}
                
                <DropdownMenuItem onClick={() => setShowEditProfile(true)}>
                  <User className="mr-2 h-4 w-4" />
                  <span>{t('settings.editProfile')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate('/pricing')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>{t('settings.manageSubscription')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => setShowPaymentHistory(true)}>
                  <Receipt className="mr-2 h-4 w-4" />
                  <span>{t('settings.paymentHistory')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={logout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('settings.logOut')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Modals */}
            <EditProfileModal
              isOpen={showEditProfile}
              onClose={() => setShowEditProfile(false)}
              user={user}
              onUpdate={refreshUser}
            />
            <PaymentHistoryModal
              isOpen={showPaymentHistory}
              onClose={() => setShowPaymentHistory(false)}
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full overflow-hidden">
          <TabsList className="flex w-full justify-start mb-6 sm:mb-8 gap-1 sm:gap-2 overflow-x-auto pb-2 scrollbar-hide px-1">
            <TabsTrigger value="dashboard" className="text-xs md:text-sm flex-shrink-0 whitespace-nowrap">
              <Activity className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="hidden md:inline">{t('home.dashboard')}</span>
              <span className="md:hidden">{t('nav.home')}</span>
            </TabsTrigger>
            <TabsTrigger value="nutrition" className="text-xs md:text-sm flex-shrink-0 whitespace-nowrap">
              <UtensilsCrossed className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="hidden md:inline">{t('home.nutrition')}</span>
              <span className="md:hidden">{t('health.food')}</span>
            </TabsTrigger>
            {user?.gender?.toLowerCase() === 'female' && (
              <TabsTrigger value="womens-health" className="text-xs md:text-sm flex-shrink-0 whitespace-nowrap">
                <Heart className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="hidden md:inline">{t('health.womensHealth')}</span>
                <span className="md:hidden">{t('health.womensHealth')}</span>
              </TabsTrigger>
            )}
            
            <TabsTrigger value="history" className="text-xs md:text-sm flex-shrink-0 whitespace-nowrap">
              <CalendarIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="hidden md:inline">{t('home.history')}</span>
              <span className="md:hidden">{t('home.history')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-4">
            {/* Today's Summary - Clean cards */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="text-sm font-medium text-gray-500 mb-3">{t('home.todayProgress')}</h2>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                  <Droplets className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-gray-900">{healthData?.water || 0}/8</p>
                  <p className="text-xs text-gray-500">{t('home.water')}</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-xl">
                  <Flame className="h-5 w-5 text-orange-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-gray-900">{loggedFoods.reduce((sum, f) => sum + (f.calories || 0), 0)}</p>
                  <p className="text-xs text-gray-500">{t('home.calories')}</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-xl">
                  <Activity className="h-5 w-5 text-green-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-gray-900">{healthData?.workouts?.length || 0}</p>
                  <p className="text-xs text-gray-500">{t('home.workouts')}</p>
                </div>
              </div>
            </div>

            {/* Quick Actions - Clean buttons */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="text-sm font-medium text-gray-500 mb-3">{t('home.quickActions')}</h2>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShowMealPlanner(true)}
                  className="flex items-center gap-3 p-3 bg-green-50 rounded-xl hover:bg-green-100 transition-colors text-left"
                >
                  <span className="text-2xl">🥗</span>
                  <div>
                    <p className="font-medium text-gray-800">{t('home.mealPlanner')}</p>
                    <p className="text-xs text-gray-500">{t('home.planYourMeals')}</p>
                  </div>
                </button>
                <button
                  onClick={() => setShowWorkoutLibrary(true)}
                  className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors text-left"
                >
                  <span className="text-2xl">💪</span>
                  <div>
                    <p className="font-medium text-gray-800">{t('home.workouts')}</p>
                    <p className="text-xs text-gray-500">Personalized</p>
                  </div>
                </button>
                <button
                  onClick={() => setShowMedicineChecker(true)}
                  className="flex items-center gap-3 p-3 bg-red-50 rounded-xl hover:bg-red-100 transition-colors text-left"
                >
                  <span className="text-2xl">💊</span>
                  <div>
                    <p className="font-medium text-gray-800">{t('home.medicines')}</p>
                    <p className="text-xs text-gray-500">{t('home.checkInteractions')}</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    const waterInput = document.getElementById('quick-water-add');
                    if (waterInput) waterInput.click();
                  }}
                  className="flex items-center gap-3 p-3 bg-cyan-50 rounded-xl hover:bg-cyan-100 transition-colors text-left"
                >
                  <span className="text-2xl">💧</span>
                  <div>
                    <p className="font-medium text-gray-800">{t('home.logWater')}</p>
                    <p className="text-xs text-gray-500">{t('home.trackHydration')}</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Hidden water input for quick action */}
            <button id="quick-water-add" className="hidden" onClick={() => handleAddWater()}></button>

            {/* AI Food Scanner - Cleaner */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="text-sm font-medium text-gray-500 mb-3">{t('health.foodScanner')}</h2>
              <Dialog open={mealDialogOpen} onOpenChange={setMealDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-orange-600 hover:bg-orange-700">
                      <Camera className="h-4 w-4 mr-2" />
                      {t('health.scanLogMeal')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{t('health.foodAnalysis')}</DialogTitle>
                    </DialogHeader>
                    {!foodAnalysis ? (
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>{t('health.captureUploadFood')}</Label>
                          
                          {/* Camera Capture or File Upload Options */}
                          {!foodImagePreview && (
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              <label htmlFor="camera-capture" className="cursor-pointer">
                                <div className="h-full border-2 border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center hover:border-orange-400 transition-colors">
                                  <Camera className="h-8 w-8 mb-2 text-gray-600" />
                                  <span className="text-sm">{t('health.takePhoto')}</span>
                                </div>
                                {/* Using native input for camera capture attribute to work properly */}
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  onChange={handleImageSelect}
                                  className="hidden"
                                  id="camera-capture"
                                />
                              </label>
                              <label htmlFor="food-image-upload" className="cursor-pointer">
                                <div className="h-full border-2 border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center hover:border-orange-400 transition-colors">
                                  <Upload className="h-8 w-8 mb-2 text-gray-600" />
                                  <span className="text-sm">{t('health.uploadPhoto')}</span>
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleImageSelect}
                                  className="hidden"
                                  id="food-image-upload"
                                />
                              </label>
                            </div>
                          )}
                          
                          {/* Image Preview */}
                          {foodImagePreview && (
                            <div className="space-y-3">
                              <div className="border-2 border-gray-300 rounded-lg p-4">
                                <img 
                                  src={foodImagePreview} 
                                  alt="Food preview" 
                                  className="max-h-64 mx-auto rounded-lg shadow-md"
                                />
                              </div>
                              <Button 
                                onClick={() => {
                                  setFoodImage(null);
                                  setFoodImagePreview(null);
                                }}
                                variant="outline"
                                className="w-full"
                              >
                                Remove Image
                              </Button>
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-500 text-center">
                            Take a photo or upload an image (JPG, PNG, WEBP)
                          </p>
                        </div>
                        
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-gray-500">Or</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t('health.describeMeal')}</Label>
                          <Textarea
                            placeholder={t('health.mealPlaceholder')}
                            value={mealDescription}
                            onChange={(e) => setMealDescription(e.target.value)}
                            rows={4}
                          />
                          <p className="text-xs text-gray-500">
                            💬 {t('health.describeMeal')}
                          </p>
                        </div>
                        
                        <Button 
                          onClick={handleAnalyzeFood} 
                          disabled={processingFood || (!mealDescription.trim() && !foodImage)} 
                          className="w-full bg-orange-600 hover:bg-orange-700"
                        >
                          {processingFood ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('health.analyzing')}</>
                          ) : (
                            <><Camera className="h-4 w-4 mr-2" />{t('health.analyzeFood')}</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4 py-4">
                        {/* Food Analysis Preview Card */}
                        {parsedFoodData && (
                          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="font-semibold text-lg text-gray-800">{parsedFoodData.name}</h3>
                              {parsedFoodData.healthScore && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  parsedFoodData.healthScore >= 7 ? 'bg-green-100 text-green-700' :
                                  parsedFoodData.healthScore >= 4 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  Health Score: {parsedFoodData.healthScore}/10
                                </span>
                              )}
                            </div>
                            
                            {/* Nutrition Grid */}
                            <div className="grid grid-cols-3 gap-2 mb-3">
                              <div className="bg-white/80 rounded-lg p-2 text-center">
                                <p className="text-xl font-bold text-orange-600">{parsedFoodData.calories}</p>
                                <p className="text-xs text-gray-500">{t('home.calories')}</p>
                              </div>
                              <div className="bg-white/80 rounded-lg p-2 text-center">
                                <p className="text-xl font-bold text-blue-600">{parsedFoodData.protein}g</p>
                                <p className="text-xs text-gray-500">{t('health.protein')}</p>
                              </div>
                              <div className="bg-white/80 rounded-lg p-2 text-center">
                                <p className="text-xl font-bold text-teal-600">{parsedFoodData.carbs}g</p>
                                <p className="text-xs text-gray-500">{t('health.carbs')}</p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2">
                              <div className="bg-white/60 rounded-lg p-2 text-center">
                                <p className="text-sm font-medium text-gray-700">{parsedFoodData.fat}g</p>
                                <p className="text-xs text-gray-500">{t('health.fats')}</p>
                              </div>
                              <div className="bg-white/60 rounded-lg p-2 text-center">
                                <p className="text-sm font-medium text-gray-700">{parsedFoodData.fiber}g</p>
                                <p className="text-xs text-gray-500">{t('health.fiber')}</p>
                              </div>
                              <div className="bg-white/60 rounded-lg p-2 text-center">
                                <p className="text-sm font-medium text-gray-700">{parsedFoodData.sugar}g</p>
                                <p className="text-xs text-gray-500">{t('health.sugar')}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* AI Analysis Details (collapsed) */}
                        <details className="group">
                          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
                            <span>View full AI analysis</span>
                            <ChevronRight className="h-4 w-4 group-open:rotate-90 transition-transform" />
                          </summary>
                          <div className="mt-2 prose prose-sm max-w-none whitespace-pre-wrap bg-gray-50 p-3 rounded-lg border text-sm">
                            {foodAnalysis}
                          </div>
                        </details>
                        
                        {/* Action Buttons */}
                        <div className="flex space-x-2 pt-2">
                          <Button 
                            onClick={handleClearFoodAnalysis} 
                            variant="outline" 
                            className="flex-1"
                          >
                            {t('health.tryAgain')}
                          </Button>
                          <Button 
                            onClick={handleAddFoodToDiary} 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            {t('health.addToDiary')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                <p className="text-sm text-gray-500 mt-2">
                  📷 {t('health.snapPhoto')}
                </p>
            </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Medical Assistant - New Comprehensive UI */}
            <HealthAssistantUI />

            {/* Medicine Reminders */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="flex items-center">
                  <Pill className="h-5 w-5 mr-2 text-teal-600" />
                  {t('health.medicines')}
                </CardTitle>
                <Dialog open={medicineDialogOpen} onOpenChange={setMedicineDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700">+ {t('common.add')}</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('health.addMedicine')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>{t('health.medicineName')}</Label>
                        <Input placeholder={t('health.medicineNamePlaceholder')} value={newMedicine.name} onChange={(e) => setNewMedicine({ ...newMedicine, name: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('health.dosage')}</Label>
                        <Input placeholder={t('health.dosagePlaceholder')} value={newMedicine.dosage} onChange={(e) => setNewMedicine({ ...newMedicine, dosage: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('health.frequency')}</Label>
                        <Select value={newMedicine.frequency} onValueChange={(value) => setNewMedicine({ ...newMedicine, frequency: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Daily">{t('health.daily')}</SelectItem>
                            <SelectItem value="Twice Daily">{t('health.twiceDaily')}</SelectItem>
                            <SelectItem value="Weekly">{t('health.weekly')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('health.time')}</Label>
                        <Input type="time" value={newMedicine.time} onChange={(e) => setNewMedicine({ ...newMedicine, time: e.target.value })} />
                      </div>
                      <Button onClick={handleAddMedicine} className="w-full bg-teal-600 hover:bg-teal-700">{t('health.addMedicine')}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="space-y-3">
                {medicines.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Pill className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>{t('health.noMedicinesAdded')}</p>
                    <p className="text-sm mt-2">{t('health.addMedicinesToGetReminders')}</p>
                  </div>
                ) : (
                  medicines.map((medicine) => {
                    const isTakenToday = medicine.lastTaken && 
                      new Date(medicine.lastTaken).toDateString() === new Date().toDateString();
                    const streak = medicine.streak || 0;
                    
                    return (
                      <div 
                        key={medicine.id} 
                        className={`p-4 border-2 rounded-xl transition-all duration-300 ${
                          isTakenToday 
                            ? 'border-green-300 bg-green-50/50' 
                            : 'border-gray-200 hover:border-green-200 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900">{medicine.name}</h3>
                              {/* 🧠 Emotional Mode: Hide streak counter to reduce pressure */}
                              {streak >= 3 && shouldShowStreaks() && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                                  🔥 {streak} days
                                </span>
                              )}
                              {isTakenToday && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                  ✓ Today
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              💊 {medicine.dosage} • ⏰ {medicine.frequency} at {medicine.time}
                            </p>
                            {medicine.lastTaken && (
                              <p className="text-xs text-gray-500 mt-1">
                                Last: {new Date(medicine.lastTaken).toLocaleString([], { 
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                                })}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button 
                              variant={isTakenToday ? "secondary" : "default"}
                              size="sm" 
                              className={`${
                                isTakenToday 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-green-600 text-white hover:bg-green-700'
                              } transition-all duration-200 font-medium`}
                              onClick={() => handleMarkTaken(medicine)}
                            >
                              {isTakenToday ? ('✓ ' + (t('health.taken') || 'Taken')) : (t('health.takeNow') || 'Take Now')}
                            </Button>
                            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-500" onClick={() => handleDeleteMedicine(medicine)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('health.quickActions') || 'Quick Actions'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* 🌈 Mood Logger - NEW */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-gradient-to-r from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100 border-pink-200" 
                  size="sm"
                  onClick={() => setMoodLoggerOpen(true)}
                >
                  <Smile className="h-4 w-4 mr-2 text-pink-500" />
                  Log Mood
                </Button>

                <Dialog open={waterDialogOpen} onOpenChange={setWaterDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" size="sm">
                      <Droplets className="h-4 w-4 mr-2" />
                      {t('health.addWater')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('health.addWater')}</DialogTitle>
                    </DialogHeader>
                    <div className="py-6 text-center space-y-4">
                      <Droplets className="h-16 w-16 mx-auto text-teal-500" />
                      <p className="text-lg">{t('health.addWater')}?</p>
                      <p className="text-sm text-gray-500">{t('health.waterIntake')}: {healthData?.water || 0} {t('common.glasses')}</p>
                      <Button onClick={handleAddWater} className="w-full bg-teal-600 hover:bg-teal-700">💧 {t('health.addWater')}</Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={workoutDialogOpen} onOpenChange={setWorkoutDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" size="sm">
                      <Activity className="h-4 w-4 mr-2" />
                      {t('health.logWorkout')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('health.logWorkout')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>{t('life.type')}</Label>
                        <Input placeholder="e.g., Running, Yoga, Gym" value={workoutData.type} onChange={(e) => setWorkoutData({ ...workoutData, type: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('health.duration')} ({t('health.minutes')})</Label>
                        <Input type="number" placeholder="30" value={workoutData.duration} onChange={(e) => setWorkoutData({ ...workoutData, duration: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('health.burned')} ({t('common.cal')})</Label>
                        <Input type="number" placeholder="200" value={workoutData.caloriesBurned} onChange={(e) => setWorkoutData({ ...workoutData, caloriesBurned: e.target.value })} />
                      </div>
                      <Button onClick={handleLogWorkout} className="w-full bg-green-600 hover:bg-green-700">🏋️ {t('health.logWorkout')}</Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={symptomDialogOpen} onOpenChange={setSymptomDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" size="sm">
                      <Heart className="h-4 w-4 mr-2" />
                      {t('health.logSymptoms') || 'Log Symptoms'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('health.logSymptoms') || 'Log Symptoms'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>{t('health.symptoms') || 'Symptoms'}</Label>
                        <Textarea placeholder={t('health.describeSymptomsPlaceholder') || 'Describe your symptoms...'} value={symptomData.symptoms} onChange={(e) => setSymptomData({ ...symptomData, symptoms: e.target.value })} rows={3} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('health.severity') || 'Severity'}</Label>
                        <Select value={symptomData.severity} onValueChange={(value) => setSymptomData({ ...symptomData, severity: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mild">{t('health.mild') || 'Mild'}</SelectItem>
                            <SelectItem value="moderate">{t('health.moderate') || 'Moderate'}</SelectItem>
                            <SelectItem value="severe">{t('health.severe') || 'Severe'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('health.notesOptional') || 'Notes (optional)'}</Label>
                        <Textarea placeholder={t('health.additionalNotesPlaceholder') || 'Any additional notes...'} value={symptomData.notes} onChange={(e) => setSymptomData({ ...symptomData, notes: e.target.value })} rows={2} />
                      </div>
                      <Button onClick={handleLogSymptom} className="w-full bg-red-600 hover:bg-red-700">🌡️ {t('health.logSymptoms') || 'Log Symptoms'}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>
        </div>
      </TabsContent>

      {/* Nutrition Tab */}
      <TabsContent value="nutrition" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <UtensilsCrossed className="h-5 w-5 mr-2 text-green-600" />
              {t('health.nutritionTracker')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Today's Nutrition Summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-orange-900">{t('home.calories')}</span>
                  <Flame className="h-5 w-5 text-orange-600" />
                </div>
                <p className="text-2xl font-bold text-orange-900">{loggedFoods.reduce((sum, f) => sum + (f.calories || 0), 0)}</p>
                <p className="text-xs text-orange-600 mt-1">{t('health.ofGoal')} 2000</p>
                <Progress value={(loggedFoods.reduce((sum, f) => sum + (f.calories || 0), 0) / 2000) * 100} className="mt-2 h-2" />
              </div>
              
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">{t('health.protein')}</span>
                  <span className="text-lg">🥩</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">{loggedFoods.reduce((sum, f) => sum + (f.protein || 0), 0)}g</p>
                <p className="text-xs text-blue-600 mt-1">{t('health.ofGoal')} 150g</p>
                <Progress value={(loggedFoods.reduce((sum, f) => sum + (f.protein || 0), 0) / 150) * 100} className="mt-2 h-2" />
              </div>

              <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-900">{t('health.carbs')}</span>
                  <span className="text-lg">🍞</span>
                </div>
                <p className="text-2xl font-bold text-green-900">{loggedFoods.reduce((sum, f) => sum + (f.carbs || 0), 0)}g</p>
                <p className="text-xs text-green-600 mt-1">{t('health.ofGoal')} 250g</p>
                <Progress value={(loggedFoods.reduce((sum, f) => sum + (f.carbs || 0), 0) / 250) * 100} className="mt-2 h-2" />
              </div>

              <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-900">{t('health.fats')}</span>
                  <span className="text-lg">🥑</span>
                </div>
                <p className="text-2xl font-bold text-yellow-900">{loggedFoods.reduce((sum, f) => sum + (f.fats || 0), 0)}g</p>
                <p className="text-xs text-yellow-600 mt-1">{t('health.ofGoal')} 70g</p>
                <Progress value={(loggedFoods.reduce((sum, f) => sum + (f.fats || 0), 0) / 70) * 100} className="mt-2 h-2" />
              </div>

              <div className="bg-teal-50 p-4 rounded-xl border border-teal-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-teal-900">{t('health.fiber')}</span>
                  <span className="text-lg">🥬</span>
                </div>
                <p className="text-2xl font-bold text-teal-900">{loggedFoods.reduce((sum, f) => sum + (f.fiber || 0), 0)}g</p>
                <p className="text-xs text-teal-600 mt-1">{t('health.ofGoal')} 30g</p>
                <Progress value={(loggedFoods.reduce((sum, f) => sum + (f.fiber || 0), 0) / 30) * 100} className="mt-2 h-2" />
              </div>

              <div className="bg-cyan-50 p-4 rounded-xl border border-cyan-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-pink-900">{t('health.sugar')}</span>
                  <span className="text-lg">🍬</span>
                </div>
                <p className="text-2xl font-bold text-pink-900">{loggedFoods.reduce((sum, f) => sum + (f.sugar || 0), 0)}g</p>
                <p className="text-xs text-cyan-600 mt-1">{t('health.limit')} 50g</p>
                <Progress value={(loggedFoods.reduce((sum, f) => sum + (f.sugar || 0), 0) / 50) * 100} className="mt-2 h-2" />
              </div>
            </div>

            {/* Log Food Button */}
            <Dialog open={mealDialogOpen} onOpenChange={(open) => {
              setMealDialogOpen(open);
              if (!open) {
                setFoodAnalysis(null);
                setParsedFoodData(null);
                setMealDescription('');
              }
            }}>
              <DialogTrigger asChild>
                <Button className="w-full bg-green-600 hover:bg-green-700">
                  <UtensilsCrossed className="h-4 w-4 mr-2" />
                  {t('health.logFood')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{parsedFoodData ? `📊 ${t('health.foodAnalysis')}` : t('health.logFood')}</DialogTitle>
                </DialogHeader>
                
                {!foodAnalysis ? (
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>{t('health.describeMeal')}</Label>
                      <Textarea
                        placeholder={t('health.mealPlaceholder')}
                        value={mealDescription}
                        onChange={(e) => setMealDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <Button 
                      onClick={handleAnalyzeFood} 
                      disabled={processingFood || !mealDescription.trim()}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {processingFood ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t('health.analyzing')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          {t('health.analyzeFood')}
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 py-2">
                    {/* Food Preview Card */}
                    {parsedFoodData && (
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-gray-800">{parsedFoodData.name}</h3>
                          {parsedFoodData.healthScore && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              parsedFoodData.healthScore >= 7 ? 'bg-green-100 text-green-700' :
                              parsedFoodData.healthScore >= 4 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {parsedFoodData.healthScore}/10
                            </span>
                          )}
                        </div>
                        
                        {/* Nutrition Grid */}
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <div className="bg-white/80 rounded-lg p-2 text-center">
                            <p className="text-lg font-bold text-orange-600">{parsedFoodData.calories}</p>
                            <p className="text-xs text-gray-500">{t('home.calories')}</p>
                          </div>
                          <div className="bg-white/80 rounded-lg p-2 text-center">
                            <p className="text-lg font-bold text-blue-600">{parsedFoodData.protein}g</p>
                            <p className="text-xs text-gray-500">{t('health.protein')}</p>
                          </div>
                          <div className="bg-white/80 rounded-lg p-2 text-center">
                            <p className="text-lg font-bold text-teal-600">{parsedFoodData.carbs}g</p>
                            <p className="text-xs text-gray-500">{t('health.carbs')}</p>
                          </div>
                        </div>
                        
                        <div className="flex justify-between text-xs text-gray-600 px-1">
                          <span>{t('health.fats')}: {parsedFoodData.fat}g</span>
                          <span>{t('health.fiber')}: {parsedFoodData.fiber}g</span>
                          <span>{t('health.sugar')}: {parsedFoodData.sugar}g</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <Button 
                        onClick={handleClearFoodAnalysis} 
                        variant="outline" 
                        className="flex-1"
                      >
                        {t('health.tryAgain')}
                      </Button>
                      <Button 
                        onClick={handleAddFoodToDiary} 
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {t('health.addToDiary')}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Logged Foods Today */}
            {loggedFoods.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Today&apos;s Meals</h3>
                <div className="space-y-2">
                  {loggedFoods.map((food) => (
                    <div key={food.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{food.name}</p>
                          <div className="flex gap-4 mt-1 text-xs text-gray-600">
                            <span>🔥 {food.calories} cal</span>
                            <span>🥩 {food.protein}g protein</span>
                            <span>🍞 {food.carbs}g carbs</span>
                            <span>🥑 {food.fats}g fats</span>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteFood(food.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="history" className="space-y-6 w-full overflow-x-hidden">
        <HealthCalendar />
      </TabsContent>

      {/* Women's Health Tab - Only for female users */}
      {user?.gender?.toLowerCase() === 'female' && (
        <TabsContent value="womens-health" className="w-full overflow-x-hidden">
          <WomensHealth />
        </TabsContent>
      )}
    </Tabs>
      </main>

      {/* Edit Food Dialog */}
      <Dialog open={editFoodDialogOpen} onOpenChange={setEditFoodDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Food Entry</DialogTitle>
          </DialogHeader>
          {editingFood && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Food Name</Label>
                <Input
                  value={editingFood.name}
                  onChange={(e) => setEditingFood({...editingFood, name: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Calories</Label>
                  <Input
                    type="number"
                    value={editingFood.calories}
                    onChange={(e) => setEditingFood({...editingFood, calories: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Protein (g)</Label>
                  <Input
                    type="number"
                    value={editingFood.protein}
                    onChange={(e) => setEditingFood({...editingFood, protein: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Carbs (g)</Label>
                  <Input
                    type="number"
                    value={editingFood.carbs}
                    onChange={(e) => setEditingFood({...editingFood, carbs: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Fat (g)</Label>
                  <Input
                    type="number"
                    value={editingFood.fat}
                    onChange={(e) => setEditingFood({...editingFood, fat: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>How healthy was this? (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={editingFood.healthScore || ''}
                  onChange={(e) => setEditingFood({...editingFood, healthScore: e.target.value})}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => setEditFoodDialogOpen(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveFoodEdit}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW: Enhanced Health Mode Modals */}
      <SmartMealPlanner
        isOpen={showMealPlanner}
        onClose={() => setShowMealPlanner(false)}
      />

      <PersonalizedWorkout
        isOpen={showWorkoutLibrary}
        onClose={() => setShowWorkoutLibrary(false)}
      />

      <MedicineInteractionChecker
        isOpen={showMedicineChecker}
        onClose={() => setShowMedicineChecker(false)}
      />
      
      {/* 🌈 Mood Logger Modal */}
      <MoodLogger
        isOpen={moodLoggerOpen}
        onClose={() => setMoodLoggerOpen(false)}
        onMoodLogged={() => {
          // Refresh health data after mood logged
          loadHealthData();
        }}
      />
      
      {/* Quick Action Button - Available on All Pages */}
      <QuickActionButton />
    </div>
  );
};

export default HealthMode;
